import { useMemo, useState, useEffect } from "react";
import type { LightingValues, SensorValues, SensorThresholdField } from "../../components/types";

type ValidationErrors = Record<string, string>;

export const LIGHTING_FIELD_CONFIG: Record<keyof LightingValues, { min: number; max: number }> = {
  rampUpTime: { min: 0, max: 120 },
  rampDownTime: { min: 0, max: 120 },
  dimLevel: { min: 0, max: 100 },
  brightLevel: { min: 0, max: 100 },
  holdTime: { min: 0, max: 180 },
};

export const SENSOR_THRESHOLD_CONFIG: Record<SensorThresholdField, { min: number; max: number }> = {
  lowerThreshold: { min: 0, max: 100 },
  upperThreshold: { min: 0, max: 100 },
};

const PAIRED_FIELDS: Record<string, string[]> = {
  rampUpTime: ["rampDownTime"],
  rampDownTime: ["rampUpTime"],
  dimLevel: ["brightLevel"],
  brightLevel: ["dimLevel"],
  lowerThreshold: ["upperThreshold"],
  upperThreshold: ["lowerThreshold"],
};

export function sanitizeNumericInput(raw: any, min: number, max: number): number {
  const trimmed = String(raw).trim();

  if (trimmed === "") return min;

  if (trimmed.startsWith("-")) return min;

  if (/[^0-9]/.test(trimmed)) return min;

  const num = Number(trimmed);
  if (isNaN(num)) return min;

  return Math.min(Math.max(num, min), max);
}

export function computeGeneralConfigErrors(
  lightingValues: LightingValues,
  sensorValues: SensorValues,
): ValidationErrors {
  const errs: ValidationErrors = {};

  if (lightingValues.rampUpTime !== null && lightingValues.rampDownTime === null) {
    errs.rampDownTime = "Ramp Down Time is required";
  }
  if (lightingValues.rampDownTime !== null && lightingValues.rampUpTime === null) {
    errs.rampUpTime = "Ramp Up Time is required";
  }
  if (lightingValues.dimLevel !== null && lightingValues.brightLevel === null) {
    errs.brightLevel = "Bright Level is required";
  }
  if (lightingValues.brightLevel !== null && lightingValues.dimLevel === null) {
    errs.dimLevel = "Dim Level is required";
  }
  if (sensorValues.lowerThreshold !== null && sensorValues.upperThreshold === null) {
    errs.upperThreshold = "Upper Threshold is required";
  }
  if (sensorValues.upperThreshold !== null && sensorValues.lowerThreshold === null) {
    errs.lowerThreshold = "Lower Threshold is required";
  }
  if (sensorValues.isPhotoSensing) {
    if (sensorValues.lowerThreshold === null) {
      errs.lowerThreshold = "Lower Threshold is required";
    }
    if (sensorValues.upperThreshold === null) {
      errs.upperThreshold = "Upper Threshold is required";
    }
  }

  if (
    sensorValues.lowerThreshold !== null &&
    sensorValues.upperThreshold !== null
  ) {
    if (sensorValues.upperThreshold <= sensorValues.lowerThreshold) {
      errs.upperThreshold = "Upper Threshold must be greater than Lower Threshold";
    }
  }

  if (
    lightingValues.dimLevel !== null &&
    lightingValues.brightLevel !== null
  ) {
    if (lightingValues.brightLevel <= lightingValues.dimLevel) {
      errs.brightLevel = "Bright Level must be greater than Dim Level";
    }
  }

  const lc = LIGHTING_FIELD_CONFIG;
  if (lightingValues.rampUpTime !== null && (lightingValues.rampUpTime < lc.rampUpTime.min || lightingValues.rampUpTime > lc.rampUpTime.max)) {
    errs.rampUpTime = `Value must be between ${lc.rampUpTime.min} and ${lc.rampUpTime.max}`;
  }
  if (lightingValues.rampDownTime !== null && (lightingValues.rampDownTime < lc.rampDownTime.min || lightingValues.rampDownTime > lc.rampDownTime.max)) {
    errs.rampDownTime = `Value must be between ${lc.rampDownTime.min} and ${lc.rampDownTime.max}`;
  }
  if (lightingValues.dimLevel !== null && (lightingValues.dimLevel < lc.dimLevel.min || lightingValues.dimLevel > lc.dimLevel.max)) {
    errs.dimLevel = `Value must be between ${lc.dimLevel.min} and ${lc.dimLevel.max}`;
  }
  if (lightingValues.brightLevel !== null && (lightingValues.brightLevel < lc.brightLevel.min || lightingValues.brightLevel > lc.brightLevel.max)) {
    errs.brightLevel = `Value must be between ${lc.brightLevel.min} and ${lc.brightLevel.max}`;
  }
  if (lightingValues.holdTime !== null && (lightingValues.holdTime < lc.holdTime.min || lightingValues.holdTime > lc.holdTime.max)) {
    errs.holdTime = `Value must be between ${lc.holdTime.min} and ${lc.holdTime.max}`;
  }

  const stc = SENSOR_THRESHOLD_CONFIG;
  if (sensorValues.lowerThreshold !== null && (sensorValues.lowerThreshold < stc.lowerThreshold.min || sensorValues.lowerThreshold > stc.lowerThreshold.max)) {
    errs.lowerThreshold = `Value must be between ${stc.lowerThreshold.min} and ${stc.lowerThreshold.max}`;
  }
  if (sensorValues.upperThreshold !== null && (sensorValues.upperThreshold < stc.upperThreshold.min || sensorValues.upperThreshold > stc.upperThreshold.max)) {
    errs.upperThreshold = `Value must be between ${stc.upperThreshold.min} and ${stc.upperThreshold.max}`;
  }

  return errs;
}

export function useGeneralConfigurationValidation(
  lightingValues: LightingValues,
  sensorValues: SensorValues,
) {
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [visibleErrorFields, setVisibleErrorFields] = useState<Set<string>>(new Set());

  const touchField = (fieldName: string) => {
    setVisibleErrorFields((prev) => {
      const next = new Set(prev);
      next.add(fieldName);
      const paired = PAIRED_FIELDS[fieldName];
      if (paired) {
        paired.forEach((f) => next.add(f));
      }
      return next;
    });
  };

  useEffect(() => {
    const errs = computeGeneralConfigErrors(lightingValues, sensorValues);
    setErrors(errs);
  }, [lightingValues, sensorValues]);

  const validateForm = (): boolean => {
    const allFields = [
      "rampUpTime",
      "rampDownTime",
      "dimLevel",
      "brightLevel",
      "holdTime",
      "lowerThreshold",
      "upperThreshold",
    ];
    setVisibleErrorFields(new Set(allFields));
    const errs = computeGeneralConfigErrors(lightingValues, sensorValues);
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const displayedErrors = useMemo(() => {
    const result: ValidationErrors = {};
    visibleErrorFields.forEach((field) => {
      if (errors[field]) {
        result[field] = errors[field];
      }
    });
    return result;
  }, [errors, visibleErrorFields]);

  const setFieldError = (fieldName: string, message: string) => {
    setErrors((prev) => ({ ...prev, [fieldName]: message }));
    setVisibleErrorFields((prev) => new Set(prev).add(fieldName));
  };

  return {
    errors,
    visibleErrorFields,
    displayedErrors,
    touchField,
    validateForm,
    setFieldError,
  };
}
